import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { CATEGORIES, PRODUCTS } from "@/lib/catalog";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ContactCta } from "@/components/site/ContactCta";
import winkelImg from "@/assets/winkel-shop-2.png.asset.json";
import { ProductCard } from "@/components/shop/ProductCard";
import { WebshopGate } from "@/components/shop/WebshopGate";
import { ChevronDown, ChevronLeft, ChevronRight, SlidersHorizontal, X } from "lucide-react";

const PER_PAGE_OPTIONS = [10, 50, 100] as const;
type PerPage = (typeof PER_PAGE_OPTIONS)[number];

const shopSearchSchema = z.object({
  q: fallback(z.string(), "").default(""),
});

export const Route = createFileRoute("/c/")({
  validateSearch: zodValidator(shopSearchSchema),
  head: () => ({
    meta: [
      { title: "Online bestellen — Elvion Group Nederland" },
      {
        name: "description",
        content:
          "Online bestellen bij Elvion Group Nederland: elektro, beveiliging, netwerken en verlichting. Goedgekeurde zakelijke klanten zien hun nettoprijs direct.",
      },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: "Online bestellen — Elvion Group Nederland" },
      {
        property: "og:description",
        content:
          "Professionele installatiematerialen direct uit voorraad. Filter op categorie en blader door het hele assortiment.",
      },
    ],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/c" }],
  }),
  component: () => (
    <WebshopGate>
      <ShopIndex />
    </WebshopGate>
  ),
});

function ShopIndex() {
  const { q } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [activeCat, setActiveCat] = useState<string | undefined>(undefined);
  const [activeSub, setActiveSub] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [per, setPer] = useState<PerPage>(10);
  const [mobileOpen, setMobileOpen] = useState(false);

  const query = q.trim().toLowerCase();
  const queryDigits = query.replace(/\D+/g, "");
  const isNumericQuery = queryDigits.length >= 6 && queryDigits.length === query.replace(/\s+/g, "").length;

  const filtered = useMemo(() => {
    return PRODUCTS.filter((p) => {
      if (activeCat && p.category !== activeCat) return false;
      if (activeSub && p.subcategory !== activeSub) return false;
      if (query) {
        // EAN/GTIN match: negeer spaties/streepjes in de zoekopdracht
        if (isNumericQuery && p.gtin && p.gtin.replace(/\D+/g, "").includes(queryDigits)) {
          return true;
        }
        const hay = [
          p.name,
          p.brand,
          p.category_label,
          p.spec_tag,
          p.short_description,
          p.mpn ?? "",
          p.gtin ?? "",
        ]
          .join(" ")
          .toLowerCase();
        if (!hay.includes(query)) return false;
      }
      return true;
    });
  }, [activeCat, activeSub, query, queryDigits, isNumericQuery]);


  const totalPages = Math.max(1, Math.ceil(filtered.length / per));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * per;
  const pageItems = filtered.slice(start, start + per);

  const cat = activeCat ? CATEGORIES.find((c) => c.slug === activeCat) : undefined;
  const sub = cat && activeSub ? cat.subcategories.find((s) => s.slug === activeSub) : undefined;

  const clearSearch = () => {
    navigate({ search: { q: "" } });
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [query]);

  const selectCategory = (slug?: string) => {
    setActiveCat(slug);
    setActiveSub(undefined);
    setPage(1);
  };
  const selectSub = (catSlug: string, subSlug: string) => {
    setActiveCat(catSlug);
    setActiveSub(subSlug);
    setPage(1);
  };

  return (
    <>
      <Header variant="shop" />
      <ContactCta
        imageUrl={winkelImg.url}
        imageAlt="Klant krijgt persoonlijk advies in de Elvion Group Nederland winkel"
        imageLeft
        title="Alles voor elektrotechniek, beveiliging en verlichting"
        description="Van groepenkasten tot camerabeveiliging en van netwerkcomponenten tot LED-verlichting. Bij Elvion Group Nederland vind je een breed assortiment installatiematerialen voor elke toepassing, zowel voor particuliere als zakelijke klanten."
        bullets={[
          "Ruim assortiment topmerken op voorraad",
          "Snelle levering direct uit eigen magazijn",
          "Deskundig advies bij elke aankoop",
        ]}
        primaryCta={{ label: "Advies aanvragen", to: "/advies" }}
        secondaryCta={{ label: "Neem contact op", to: "/klantenservice" }}
      />
      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">


        {query && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-card px-4 py-3">
            <p className="text-sm text-foreground">
              {filtered.length} {filtered.length === 1 ? "resultaat" : "resultaten"} voor{" "}
              <span className="font-semibold">"{q}"</span>
            </p>
            <button
              type="button"
              onClick={clearSearch}
              className="inline-flex items-center gap-1 text-sm font-semibold text-orange hover:underline"
            >
              <X className="h-4 w-4" /> Wis zoekopdracht
            </button>
          </div>
        )}

        <a id="assortiment" className="sr-only" href="#assortiment" tabIndex={-1}>
          Assortiment
        </a>
        <h1 className="sr-only">
          {sub?.name ?? cat?.name ?? "Assortiment"}
        </h1>
        {cat?.description && (
          <p className="mt-2 max-w-3xl text-sm text-text-secondary sm:text-base">
            {cat.description}
          </p>
        )}


        {/* Mobile filter toggle */}
        <div className="mt-6 lg:hidden">
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm font-semibold text-foreground"
          >
            <SlidersHorizontal className="h-4 w-4" /> Categorieën
            <ChevronDown
              className={`h-4 w-4 transition-transform ${mobileOpen ? "rotate-180" : ""}`}
            />
          </button>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[260px_1fr]">
          <aside
            className={`lg:sticky lg:top-24 lg:self-start ${mobileOpen ? "block" : "hidden lg:block"}`}
          >
            {/* Spacer to align with the product grid's top toolbar on desktop */}
            <div aria-hidden className="mb-4 hidden h-[34px] lg:block" />
            <CategorySidebar
              activeCat={activeCat}
              activeSub={activeSub}
              onSelectCategory={selectCategory}
              onSelectSub={selectSub}
            />
          </aside>

          <div>
            <div className="mb-4 flex flex-wrap items-center justify-end gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-text-secondary">
                Per pagina
                <select
                  value={per}
                  onChange={(e) => {
                    setPer(Number(e.target.value) as PerPage);
                    setPage(1);
                  }}
                  className="rounded-md border border-border bg-background px-2 py-1.5 text-sm font-semibold text-foreground focus:border-orange focus:outline-none"
                >
                  {PER_PAGE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {pageItems.length === 0 ? (
              <p className="rounded-md border border-border bg-card p-6 text-sm text-text-secondary">
                Geen producten gevonden in deze selectie.
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                {pageItems.map((p) => (
                  <ProductCard key={p.slug} product={p} />
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onChange={(p) => {
                  setPage(p);
                  if (typeof window !== "undefined") {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }}
              />
            )}
          </div>
        </div>

        {!activeCat && !activeSub && (
          <article className="mt-16 max-w-4xl space-y-6 border-t border-border pt-10 text-text-secondary">
            <h2 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Elektrotechniek, beveiliging, netwerken en verlichting
            </h2>
            <p>
              Elektrotechniek, beveiliging, netwerken en verlichting zijn onmisbaar binnen woningen,
              bedrijfspanden en industriële installaties. In deze categorie vindt u een uitgebreid
              assortiment producten voor installatie, uitbreiding, onderhoud en renovatie van
              elektrische en technische systemen.
            </p>
            <p>
              Het aanbod bestaat uit groepenkasten, beveiligingscamera's, netwerkapparatuur,
              verlichting, schakelmateriaal, bekabeling, gereedschap en installatiematerialen.
              Geschikt voor zowel particuliere gebruikers als professionele installateurs, technische
              diensten en zakelijke organisaties.
            </p>

            <section>
              <h3 className="text-xl font-bold text-foreground">Elektrotechniek</h3>
              <p className="mt-2">
                Voor elektrotechnische installaties zijn groepenkasten, aardlekschakelaars,
                installatieautomaten, kabels, connectoren en diverse montagematerialen beschikbaar.
                Deze producten vormen de basis voor veilige en betrouwbare stroomvoorzieningen in
                woningen en bedrijfsgebouwen.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-foreground">Camerabeveiliging</h3>
              <p className="mt-2">
                Het assortiment camerabeveiliging omvat IP-camera's, bewakingscamera's,
                NVR-recorders, accessoires en complete beveiligingssystemen van merken zoals
                Hikvision en Dahua. Geschikt voor woningbeveiliging, bedrijfsbeveiliging,
                terreinbewaking en toegangscontrole.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-foreground">Netwerken en data</h3>
              <p className="mt-2">
                Voor stabiele netwerkverbindingen zijn UTP-kabels, patchpanelen, netwerkconnectoren,
                patchkasten, wandcontactdozen en overige netwerkcomponenten beschikbaar. Deze
                producten worden toegepast in thuisnetwerken, kantooromgevingen, winkels, horeca en
                industriële locaties.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-foreground">Verlichting</h3>
              <p className="mt-2">
                Het assortiment verlichting omvat LED-verlichting, werkverlichting,
                buitenverlichting, bouwverlichting en energiezuinige verlichtingsoplossingen voor
                zowel particuliere als zakelijke toepassingen.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-bold text-foreground">Gereedschap en toebehoren</h3>
              <p className="mt-2">
                Naast technische installatiematerialen zijn ook diverse gereedschappen verkrijgbaar,
                waaronder kniptangen, krimptangen, striptangen, meetapparatuur en montagematerialen.
                Hiermee kunnen installaties professioneel en efficiënt worden uitgevoerd.
              </p>
            </section>

            <p>
              Alle producten zijn geselecteerd op kwaliteit, betrouwbaarheid en duurzaamheid, zodat
              u eenvoudig de juiste oplossing vindt voor elektrotechniek, beveiliging, netwerken en
              verlichting.
            </p>
          </article>
        )}
      </main>
      <Footer />
    </>
  );
}

function CategorySidebar({
  activeCat,
  activeSub,
  onSelectCategory,
  onSelectSub,
}: {
  activeCat?: string;
  activeSub?: string;
  onSelectCategory: (slug?: string) => void;
  onSelectSub: (catSlug: string, subSlug: string) => void;
}) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <h2 className="mb-3 text-sm font-bold uppercase tracking-wide">Categorieën</h2>
      <ul className="space-y-1">
        <li>
          <button
            type="button"
            onClick={() => onSelectCategory(undefined)}
            className={`block w-full rounded-md px-2 py-1.5 text-left text-sm font-semibold transition-colors ${
              !activeCat ? "bg-orange/10 text-orange" : "text-foreground hover:bg-muted"
            }`}
          >
            Alle producten
          </button>
        </li>
        {CATEGORIES.map((c) => {
          const isActive = activeCat === c.slug;
          return (
            <li key={c.slug}>
              <button
                type="button"
                onClick={() => onSelectCategory(c.slug)}
                className={`block w-full rounded-md px-2 py-1.5 text-left text-sm font-semibold transition-colors ${
                  isActive && !activeSub
                    ? "bg-orange/10 text-orange"
                    : "text-foreground hover:bg-muted"
                }`}
              >
                {c.name}
              </button>
              {isActive && c.subcategories.length > 0 && (
                <ul className="mt-1 ml-2 space-y-0.5 border-l border-border pl-3">
                  {c.subcategories.map((s) => (
                    <li key={s.slug}>
                      <button
                        type="button"
                        onClick={() => onSelectSub(c.slug, s.slug)}
                        className={`block w-full rounded px-2 py-1 text-left text-xs transition-colors ${
                          activeSub === s.slug
                            ? "font-semibold text-orange"
                            : "text-text-secondary hover:text-orange"
                        }`}
                      >
                        {s.name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function Pagination({
  currentPage,
  totalPages,
  onChange,
}: {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
}) {
  const pages = useMemo(
    () => buildPageList(currentPage, totalPages),
    [currentPage, totalPages],
  );

  return (
    <nav className="mt-8 flex flex-wrap items-center justify-end gap-1.5">
      <PageBtn
        disabled={currentPage === 1}
        onClick={() => onChange(currentPage - 1)}
        ariaLabel="Vorige pagina"
      >
        <ChevronLeft className="h-4 w-4" />
      </PageBtn>
      {pages.map((p, i) =>
        p === "…" ? (
          <span key={`gap-${i}`} className="px-2 text-sm text-text-secondary">
            …
          </span>
        ) : (
          <PageBtn key={p} active={p === currentPage} onClick={() => onChange(p)}>
            {p}
          </PageBtn>
        ),
      )}
      <PageBtn
        disabled={currentPage === totalPages}
        onClick={() => onChange(currentPage + 1)}
        ariaLabel="Volgende pagina"
      >
        <ChevronRight className="h-4 w-4" />
      </PageBtn>
    </nav>
  );
}

function PageBtn({
  active,
  disabled,
  ariaLabel,
  onClick,
  children,
}: {
  active?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-9 min-w-9 items-center justify-center rounded-md border px-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
        active
          ? "border-orange bg-orange text-white"
          : "border-border bg-card text-foreground hover:border-orange hover:text-orange"
      }`}
    >
      {children}
    </button>
  );
}

function buildPageList(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "…")[] = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  if (left > 2) pages.push("…");
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push("…");
  pages.push(total);
  return pages;
}
